import { useEffect, useMemo } from 'react';
import { Table, Alert, Spin, Space, Statistic, Row, Col, Card, Tag } from 'antd';

import { parseSheetToTuples } from '../../utils/parseSheetToTuples';
import {
    formatImportError,
    formatImportWarning,
    IMPORT_ERROR_CODES,
} from '../../utils/importErrorCodes';
import useBackendValidation from '../../hooks/useBackendValidation';
import './index.scss';

/**
 * Verify step.
 *
 * Flow:
 *   1. Parse the uploaded workbook locally (with detection).
 *   2. If local parse is clean, call /import/validate for DB checks.
 *   3. Reclassify backend errors:
 *        - UNKNOWN_PERIOD with a typo suggestion → blocking error ("did you mean…").
 *        - UNKNOWN_PERIOD without suggestion → silently ignored, shown in summary.
 *        - All other errors → blocking as-is.
 *   4. Render preview, errors, warnings, and a conditional detection summary.
 *   5. Emit { tuples: filteredTuples, errors, warnings } upward; Next gates on errors.
 */
const ImportValidation = ({ workbook, onResult }) => {
    const parseResult = useMemo(
        () =>
            workbook
                ? parseSheetToTuples(workbook)
                : { tuples: [], errors: [], warnings: [], info: null, ignored: null },
        [workbook],
    );

    const { tuples, errors: parseErrors, warnings: parseWarnings, info, ignored } = parseResult;

    const shouldValidate = parseErrors.length === 0 && tuples.length > 0;
    const { loading, result: backendResult, error: backendError } = useBackendValidation(
        tuples,
        shouldValidate,
    );

    // Split backend errors into "blocking" (typos, unknown scenario/account, etc.)
    // and "ignored" (unknown periods without a suggestion — user has a stray column).
    const { blockingBackendErrors, silentlyIgnoredPeriods } = useMemo(() => {
        const backendErrors = backendResult?.errors ?? [];
        const blocking = [];
        const ignoredPeriods = [];

        for (const err of backendErrors) {
            if (err.code === IMPORT_ERROR_CODES.UNKNOWN_PERIOD && !err.details?.suggestion) {
                ignoredPeriods.push(err);
            } else {
                blocking.push(err);
            }
        }

        return { blockingBackendErrors: blocking, silentlyIgnoredPeriods: ignoredPeriods };
    }, [backendResult]);

    // Filter tuples to exclude those whose period was silently ignored, so Execute
    // doesn't try to write them (and doesn't get rejected by the safety-net validation).
    const filteredTuples = useMemo(() => {
        if (!silentlyIgnoredPeriods.length) return tuples;
        const ignoredKeys = new Set(
            silentlyIgnoredPeriods.map((e) => `${e.row}:${e.col}`),
        );
        return tuples.filter((t) => !ignoredKeys.has(`${t._row}:${t._col}`));
    }, [tuples, silentlyIgnoredPeriods]);

    const allErrors = useMemo(
        () => [...parseErrors, ...blockingBackendErrors],
        [parseErrors, blockingBackendErrors],
    );

    const allWarnings = useMemo(
        () => [...parseWarnings, ...(backendResult?.warnings ?? [])],
        [parseWarnings, backendResult],
    );

    useEffect(() => {
        onResult({
            tuples: filteredTuples,
            errors: allErrors,
            warnings: allWarnings,
        });
    }, [onResult, filteredTuples, allErrors, allWarnings]);

    // Group errors by a code-specific key:
    //   UNKNOWN_SCENARIO / UNKNOWN_ACCOUNT → group by value only (a missing
    //     scenario or account name is one issue regardless of which column
    //     it appears in).
    //   Everything else → group by (code, col, value) so column-specific
    //     issues like UNKNOWN_PERIOD and INVALID_NUMBER stay separate per column.
    const groupedErrors = useMemo(() => {
        const groups = new Map();
        for (const err of allErrors) {
            const groupByValueOnly =
                err.code === IMPORT_ERROR_CODES.UNKNOWN_SCENARIO ||
                err.code === IMPORT_ERROR_CODES.UNKNOWN_ACCOUNT;

            const key = groupByValueOnly
                ? `${err.code}|${err.value ?? ''}`
                : `${err.code}|${err.col ?? ''}|${err.value ?? ''}`;

            let group = groups.get(key);
            if (!group) {
                group = { representative: err, rows: [], count: 0, groupByValueOnly };
                groups.set(key, group);
            }
            group.rows.push(err.row);
            group.count += 1;
        }
        return [...groups.values()];
    }, [allErrors]);

    // Same grouping for warnings
    const groupedWarnings = useMemo(() => {
        const groups = new Map();
        for (const warn of allWarnings) {
            const key = `${warn.code}|${warn.col ?? ''}`;
            let group = groups.get(key);
            if (!group) {
                group = { representative: warn, rows: [], count: 0 };
                groups.set(key, group);
            }
            group.rows.push(warn.row);
            group.count += 1;
        }
        return [...groups.values()];
    }, [allWarnings]);

    // ----- Derive what to show in the detection summary -----
    // Unique column names from silently-ignored periods, for display
    const ignoredColumnNames = useMemo(() => {
        const set = new Set();
        for (const err of silentlyIgnoredPeriods) {
            if (err.value) set.add(err.value);
        }
        return [...set];
    }, [silentlyIgnoredPeriods]);

    const hasSummaryContent =
        !!info &&
        (info.orientation === 'rows' ||
            info.headerIndex > 1 ||
            info.entityColumn ||
            ignoredColumnNames.length > 0 ||
            ignored?.rowsAboveHeader > 0);

    // ----- Preview table (pivoted) -----
    // Rows are (scenario, account) pairs; columns are periods in first-seen order.
    // Preserves the original sheet's period ordering (monthly/quarterly/annual
    // mix shouldn't be sorted alphabetically).
    const { previewData, previewColumns } = useMemo(() => {
        // One row per (scenario, account) pair
        const rowByKey = new Map();
        // Period columns in first-appearance order
        const periodOrder = [];
        const seenPeriods = new Set();

        for (const t of filteredTuples) {
            const key = `${t.scenarioName}|${t.accountNumber}`;
            let row = rowByKey.get(key);
            if (!row) {
                row = {
                    key,
                    scenarioName: t.scenarioName,
                    accountNumber: t.accountNumber,
                };
                rowByKey.set(key, row);
            }
            row[t.period] = t.value;

            if (!seenPeriods.has(t.period)) {
                seenPeriods.add(t.period);
                periodOrder.push(t.period);
            }
        }

        const data = [...rowByKey.values()];

        const columns = [
            {
                title: 'Scenario',
                dataIndex: 'scenarioName',
                width: 140,
                fixed: 'left',
            },
            {
                title: 'Account',
                dataIndex: 'accountNumber',
                width: 140,
                fixed: 'left',
            },
            ...periodOrder.map((period) => ({
                title: period,
                dataIndex: period,
                align: 'right',
                width: 100,
                render: (v) => (v == null ? '' : v),
            })),
        ];

        return { previewData: data, previewColumns: columns };
    }, [filteredTuples]);

    const summary = backendResult?.summary;

    return (
        <div className="import-validation-preview">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {hasSummaryContent ? (
                    <Alert
                        type="info"
                        showIcon
                        message="Sheet detection"
                        description={
                            <div>
                                {info.orientation === 'rows' ? (
                                    <div>
                                        Detected as <strong>row-oriented</strong> (periods run
                                        down the side, identifiers across the top).
                                    </div>
                                ) : null}
                                {info.headerIndex > 1 ? (
                                    <div>
                                        Header found on row {info.headerIndex} (
                                        {ignored.rowsAboveHeader}{' '}
                                        {ignored.rowsAboveHeader === 1 ? 'row' : 'rows'} above
                                        were skipped).
                                    </div>
                                ) : null}
                                {info.entityColumn ? (
                                    <div>
                                        Entity column detected and ignored — entity is optional
                                        and not used for import.
                                    </div>
                                ) : null}
                                {ignoredColumnNames.length > 0 ? (
                                    <div style={{ marginTop: 8 }}>
                                        <div style={{ marginBottom: 4 }}>
                                            These columns were not recognized as periods and
                                            will be ignored:
                                        </div>
                                        <Space size={[4, 4]} wrap>
                                            {ignoredColumnNames.map((c) => (
                                                <Tag key={c}>{c}</Tag>
                                            ))}
                                        </Space>
                                    </div>
                                ) : null}
                            </div>
                        }
                    />
                ) : null}

                {summary && allErrors.length === 0 ? (
                    <Card size="small">
                        <Row gutter={16}>
                            <Col span={6}>
                                <Statistic title="Values" value={filteredTuples.length} />
                            </Col>
                            <Col span={6}>
                                <Statistic title="Scenarios" value={summary.uniqueScenarios} />
                            </Col>
                            <Col span={6}>
                                <Statistic title="Accounts" value={summary.uniqueAccounts} />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Periods"
                                    value={
                                        new Set(filteredTuples.map((t) => t.period)).size
                                    }
                                />
                            </Col>
                        </Row>
                    </Card>
                ) : null}

                {loading ? (
                    <Alert
                        message={<Spin size="small" />}
                        description="Validating against the database…"
                    />
                ) : null}

                {backendError ? (
                    <Alert
                        type="error"
                        message="Validation request failed"
                        description={backendError}
                    />
                ) : null}

                {allErrors.length ? (
                    <Alert
                        type="error"
                        showIcon
                        message={`${allErrors.length} error${allErrors.length === 1 ? '' : 's'} — fix these to continue`}
                        description={
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {groupedErrors.slice(0, 20).map((g, i) => {
                                    const { representative, count, rows, groupByValueOnly } = g;
                                    const location = groupByValueOnly
                                        ? null
                                        : representative.col != null
                                          ? `Column ${representative.col}`
                                          : 'Row-level';
                                    const rowSummary =
                                        count === 1
                                            ? `row ${rows[0]}`
                                            : `${count} rows (last: row ${rows[rows.length - 1]})`;
                                    return (
                                        <li key={i}>
                                            {location ? <strong>{location}: </strong> : null}
                                            {formatImportError(representative)}{' '}
                                            <span style={{ color: '#888' }}>
                                                — affects {rowSummary}
                                            </span>
                                        </li>
                                    );
                                })}
                                {groupedErrors.length > 20 ? (
                                    <li>…and {groupedErrors.length - 20} more issue types</li>
                                ) : null}
                            </ul>
                        }
                    />
                ) : null}

                {allWarnings.length ? (
                    <Alert
                        type="warning"
                        showIcon
                        message={`${allWarnings.length} warning${allWarnings.length === 1 ? '' : 's'}`}
                        description={
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {groupedWarnings.slice(0, 20).map((g, i) => {
                                    const { representative, count, rows } = g;
                                    const rowSummary =
                                        count === 1
                                            ? `row ${rows[0]}`
                                            : `${count} rows (last: row ${rows[rows.length - 1]})`;
                                    return (
                                        <li key={i}>
                                            {formatImportWarning(representative)}{' '}
                                            <span style={{ color: '#888' }}>
                                                — affects {rowSummary}
                                            </span>
                                        </li>
                                    );
                                })}
                                {groupedWarnings.length > 20 ? (
                                    <li>…and {groupedWarnings.length - 20} more</li>
                                ) : null}
                            </ul>
                        }
                    />
                ) : null}

                <Table
                    bordered
                    size="small"
                    pagination={{ pageSize: 50, showSizeChanger: false }}
                    columns={previewColumns}
                    dataSource={previewData}
                    scroll={{ x: 'max-content' }}
                />
            </Space>
        </div>
    );
};

export default ImportValidation;
